import React from 'react';
import { FULL_DATE_TIME_FORMAT, SHORT_DATE_FORMAT } from '../constants';
import { enumerateDaysBetweenDates } from '../utilities/helperUtility';
import Translation from '../utilities/localizationUtility';
import { dateFromParams, getTimeFrameFromDate } from '../utilities/timeUtility';
import dayjs from '../../dayjs/dayjs_timezone';
import ProductWidgetPage from './ProductWidgetPage';
import { getAvailableSlot, getTimeslot, parseSelectOptions } from './helpers';
import { StyledDatePicker, StyledGoNextMonthButton } from './ActivityClassPageParent.style';

export default class ActivityClassPageParent extends ProductWidgetPage {
  constructor(props) {
    super(props);

    this.handleDayClick = this.handleDayClick.bind(this);
    this.onDayClick = this.onDayClick.bind(this);
    this.onMonthChange = this.onMonthChange.bind(this);
    this.setupByDate = this.setupByDate.bind(this);
    this.setDefaultDates = this.setDefaultDates.bind(this);
    this.setFakeDisabledDay = this.setFakeDisabledDay.bind(this);
    this.updateSetup = this.updateSetup.bind(this);
    this.updateTimeZone = this.updateTimeZone.bind(this);
  }

  getFirstAvailableTimeslot(date, frames, allowUnavailableSlot) {
    let timeslot = null;
    let available = 0;
    const { frames: propsFrames } = this.props;
    const selectedFrame = (frames || propsFrames).find((f) => f.date === date);
    const timeslots = selectedFrame ? selectedFrame.timeslots : [];
    const selectedTimeslot = timeslots.find((t) => this.getAvailability(t) > 0);
    const isShowWaitList = this.showWaitlist();

    if (selectedTimeslot) {
      timeslot = selectedTimeslot;
      available = this.getAvailability(selectedTimeslot);
    }

    if ((isShowWaitList && !timeslot) || allowUnavailableSlot) {
      [timeslot] = timeslots; // use first timeslot if waitlist is enabled and no available timeslots
      available = this.getAvailability(timeslot);
    }

    return { timeslot, available };
  }

  getShowNextMoBtn() {
    const { currentMonth } = this.state;
    const { nextOccurrence, frames } = this.props;
    let showGoNextMonthButton = false;

    if (currentMonth) {
      const date = dayjs(currentMonth);
      showGoNextMonthButton = nextOccurrence
        && frames.filter((x) => dayjs(x.date).format('YYYY-MM') === date.format('YYYY-MM')).length === 0
        && date.isBefore(dayjs(nextOccurrence));
    }

    return showGoNextMonthButton;
  }

  // eslint-disable-next-line class-methods-use-this
  handleFramesFetched() {
    // to be overriden in ancestors
  }

  // is overriden in ClassPage component
  handleDayClick(day, modifiers = {}) {
    this.onDayClick(day, modifiers);
  }

  // eslint-disable-next-line class-methods-use-this
  handleMonthChange() {
    // to be overriden in ancestors
  }

  // eslint-disable-next-line class-methods-use-this
  handleNextMonthClick() {
    // to be overriden in ancestors
  }

  onDayClick(day, modifiers = {}, needSelectedTimeslot = false) {
    const { timeslotLoading } = this.state;

    if (modifiers.disabled || (!needSelectedTimeslot && timeslotLoading)) {
      return 'break';
    }

    const { orderActions: { setOrderDetails }, selectedDate } = this.props;
    const newSelection = dayjs(day).format(SHORT_DATE_FORMAT);

    if (newSelection !== selectedDate) {
      if (needSelectedTimeslot) {
        const { available, timeslot } = this.getFirstAvailableTimeslot(newSelection, null, true);
        setOrderDetails({
          date: newSelection,
          available,
          selectedTimeslot: timeslot,
        });
      } else {
        setOrderDetails({
          date: newSelection,
          selectedTimeslot: null,
        });
      }
    }

    return 'success';
  }

  onMonthChange(value, callback) {
    const {
      frameActions: { fetchFrames },
      selectedLocation,
      selectedResource,
      timeZone,
    } = this.props;
    const day = dayjs(value).format(SHORT_DATE_FORMAT);

    this.setState({ currentMonth: day }, () => {
      if (this.shouldFetchFrames()) {
        const { start, finish } = getTimeFrameFromDate(day, timeZone);

        fetchFrames({
          start: start.format(),
          finish: finish.format(),
          location_id: selectedLocation?.id,
          resource_id: selectedResource?.id,
        }).then((data) => {
          if (callback) {
            callback(data);
          }
          this.handleFramesFetched(data);
        });
      }
    });
  }

  shouldFetchFrames() {
    const {
      exceededBookingsLimit,
      exceededTrialPlan,
    } = this.props;
    return !exceededBookingsLimit && !exceededTrialPlan;
  }

  setDefaultDates(firstBookableDate, availableDates, needSelectOptions = false) {
    const today = dayjs().format('YYYY-MM');
    const cm = dayjs(firstBookableDate).format('YYYY-MM');

    if (today === cm) {
      // if this month is same with first bookable date's month, doesn't load frames.
      this.setState({
        availableDates,
        currentMonth: dayjs(firstBookableDate).format(SHORT_DATE_FORMAT),
        selectOptions: needSelectOptions && parseSelectOptions(firstBookableDate, this.props),
      });
    } else if (needSelectOptions) {
      // if this month is different with first bookable date's month, go to first bookable date's month.
      this.setupByDate({ date: dayjs(firstBookableDate), allowFirstSlot: true });
    } else {
      this.onMonthChange(firstBookableDate);
    }
  }

  setFakeDisabledDay(value) {
    const { frames } = this.props;
    const day = dayjs(value).format(SHORT_DATE_FORMAT);

    if (frames.some((frame) => frame.date === day)) {
      return new Date(day);
    }

    return null;
  }

  setupByDate(args) {
    const { date, allowFirstSlot = false, needCheckFrame = false } = args;
    const {
      orderActions: { setOrderDetails },
      settingActions: { setUrlParam },
      capacityType,
      maxAvailability,
    } = this.props;
    const day = date.format(SHORT_DATE_FORMAT);

    this.onMonthChange(day, (data) => {
      const frame = data.frames.find((x) => x.date === day);
      const timeslot = getTimeslot(frame, date, allowFirstSlot);

      if (timeslot) {
        const available = needCheckFrame
          ? this.getAvailability(timeslot)
          : getAvailableSlot(timeslot, capacityType, maxAvailability);

        setOrderDetails({
          date: frame.date,
          available,
          selectedTimeslot: timeslot.start,
        });
      } else if (needCheckFrame && frame) {
        const { timeSlot, available } = this.getFirstAvailableTimeslot(frame.date);
        this.setState({ currentMonth: frame.date }, () => {
          setOrderDetails({ date: frame.date, available, selectedTimeslot: timeSlot });
        });
      } else {
        this.setState({ currentMonth: null }, () => {
          setOrderDetails({ available: null });
        });
      }
    });

    if (setUrlParam) {
      setUrlParam('start', null);
    }
  }

  // is used in ancestors
  renderGoNextMonthButton() {
    const { style } = this.props;
    const t = new Translation(this.props);

    return (
      <StyledGoNextMonthButton
        styled={style.button}
        onClick={this.handleNextMonthClick}
      >
        {t.key('goNextMonth')}
      </StyledGoNextMonthButton>
    );
  }

  // is used in ancestors
  renderDatePicker(needHoverHighlight) {
    const {
      firstOccurrence,
      frames,
      lastOccurrence,
      region,
      selectedDate,
      selectedTimeslot,
      shopBeginningOfWeek,
      style,
      timeZone,
    } = this.props;
    const { currentMonth, hoverEnteredTo, hoverStart } = this.state;
    const additionalStyle = { inactive: { ...style.inactive } };
    const fromStr = dayjs(selectedTimeslot?.start || selectedTimeslot).tz(timeZone).format(FULL_DATE_TIME_FORMAT);
    const selectedDateObj = selectedDate ? dayjs(selectedDate).toDate() : null;
    const from = selectedTimeslot
      ? dayjs(fromStr).toDate()
      : dayjs(selectedDateObj).tz(timeZone).startOf('day').toDate();
    const formatStart = dayjs(hoverStart).format(SHORT_DATE_FORMAT);
    const formatEnteredTo = dayjs(hoverEnteredTo).format(SHORT_DATE_FORMAT);
    const hoverHighlight = needHoverHighlight ? (enumerateDaysBetweenDates(formatStart, formatEnteredTo)) : null;
    const to = dayjs(from).add(this.getDuration() - 1, 'seconds').toDate();
    let toMonth = null;

    if (frames.length) {
      if (dayjs(currentMonth).isAfter(dayjs(lastOccurrence))) {
        toMonth = dayjs(currentMonth).toDate();
      } else {
        toMonth = dayjs(lastOccurrence).toDate();
      }
    }

    return (
      <StyledDatePicker
        fixedWeeks
        fromMonth={dayjs(firstOccurrence).toDate()}
        handleDayMouseEnter={this.handleDayMouseEnter}
        handleDayMouseLeave={this.handleDayMouseLeave}
        modifiers={{
          selected: selectedDateObj,
          disabled: this.setDisabledDay,
          fakeDisabled: this.setFakeDisabledDay,
          hoverStart,
          hoverEnteredTo,
          hoverHighlight,
          start: from,
          end: to,
        }}
        month={dayjs(currentMonth || new Date()).toDate()}
        onDayClick={this.handleDayClick}
        onMonthChange={this.handleMonthChange}
        shopBeginningOfWeek={shopBeginningOfWeek}
        region={region}
        selectedDays={[from, { from, to }]}
        styled={{ ...style.datepicker, ...additionalStyle }}
        toMonth={toMonth}
        showOutsideDays
      />
    );
  }

  updateSetup(condition, updState, needCheckFrame = false) {
    const { urlParams, timeZone } = this.props;

    if (condition) {
      const date = dateFromParams(urlParams, timeZone);

      if (date) {
        this.setupByDate({ date, needCheckFrame });
      } else {
        this.setDefaultSetup();
      }
    } else {
      this.setState(updState);
    }
  }

  updateTimeZone(condition, isSetupByDate = false) {
    const {
      orderActions: { setOrderDetails },
      frameActions: { changeFramesTimezone },
      selectedTimeslot,
      timeZone,
    } = this.props;

    changeFramesTimezone(timeZone);

    if (condition) {
      setOrderDetails({
        date: dayjs(selectedTimeslot.start).tz(timeZone).format(SHORT_DATE_FORMAT),
      });

      if (isSetupByDate) {
        this.setupByDate({ date: dayjs(selectedTimeslot).tz(timeZone) });
      }
    }
  }
}
